/**
 * A tiny in-page signal so the open proposal editor refreshes when something
 * else in the same tab changes the proposal on the server — chiefly the
 * assistant, which edits proposals directly through its tools. The assistant
 * panel fires `notifyProposalChanged(id)` when a proposal write tool completes;
 * the editor subscribes and refetches when the id is the one it is showing.
 */

/** The assistant tools that mutate a proposal — used to spot changes in the chat stream. */
export const PROPOSAL_WRITE_TOOLS: ReadonlySet<string> = new Set([
  'renameProposal',
  'setProposalSalesperson',
  'editProposalField',
  'addProposalOption',
  'addProposalProgram',
  'updateProposalProgram',
  'removeProposalProgram',
  'addProposalDiscount',
  'removeProposalDiscount',
  'setProposalValidity',
  'removeProposalPage',
  'renameProposalPage',
  'moveProposalPage',
  'duplicateProposalPage',
  'addProposalPage',
  'addProposalContent',
  'addTemplatePage',
  'replaceProposalPage',
  'setProposalPageBlocks',
  'updateProposalBlock',
]);

type Listener = (id: string) => void;
const listeners = new Set<Listener>();

/** Announce that the proposal `id` changed on the server. */
export function notifyProposalChanged(id: string): void {
  for (const listener of [...listeners]) listener(id);
}

/** Subscribe to proposal changes; returns an unsubscribe. */
export function onProposalChanged(cb: Listener): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

/** The proposal ids a finished assistant message's write-tool outputs touched. */
export function changedProposalIds(parts: readonly unknown[] | undefined): string[] {
  const ids = new Set<string>();
  for (const part of parts ?? []) {
    const p = part as { type?: string; toolName?: string; state?: string; output?: unknown };
    const toolName = p.toolName ?? (typeof p.type === 'string' ? p.type.replace(/^tool-/, '') : '');
    if (!PROPOSAL_WRITE_TOOLS.has(toolName) || p.state !== 'output-available') continue;
    const proposal = (p.output as { proposal?: { id?: unknown } } | undefined)?.proposal;
    if (proposal && typeof proposal.id === 'string') ids.add(proposal.id);
  }
  return [...ids];
}
